import { FC, HTMLAttributes, KeyboardEvent, useEffect, useState } from 'react'

export interface CartNumberInputProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  value: number
  onChange: any
  input?: any
  max: number
  min?: number
  className?: string
  disabled?: boolean
}

export const CartNumberInput: FC<CartNumberInputProps> = ({ value, onChange, input, max, min = 1, className, disabled = false, ...rest }) => {
  const [inputValue, setInputValue] = useState(value.toString())

  // 同步外部 value 到内部 inputValue
  useEffect(() => {
    setInputValue(value.toString())
  }, [value])

  const onClickMinus = () => {
    if (value > min && !disabled) {
      const newValue = value - 1
      onChange(newValue)
    }
  }

  const onClickAdd = () => {
    if (value < max && !disabled) {
      const newValue = value + 1
      onChange(newValue)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // 只允许数字输入
    const newValue = e.target.value.replace(/[^\d]/g, '')
    setInputValue(newValue)
  }

  const handleBlur = () => {
    const numValue = parseInt(inputValue) || min
    const clampedValue = Math.max(min, Math.min(max, numValue))

    // 只有当值真正改变时才触发更新
    if (clampedValue !== value) {
      onChange(clampedValue)
    } else {
      // 即使值没变，也要更新显示（处理无效输入的情况）
      setInputValue(clampedValue.toString())
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleBlur()
    }
  }

  return (
    <div {...rest} className={`flex w-fit flex-row items-center justify-center rounded-lg border border-neutral-300 ${className || ''}`}>
      <button
        aria-label='Decrease number'
        onClick={onClickMinus}
        disabled={value === min || disabled}
        className='px-3 text-lg disabled:cursor-not-allowed disabled:text-neutral-400'
      >
        -
      </button>
      <input
        {...input}
        type='text'
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className='flex w-12 bg-transparent text-center font-medium focus:outline-none focus:ring-1 focus:ring-yellow-400 disabled:cursor-not-allowed'
      />
      <button
        aria-label='Increase number'
        onClick={onClickAdd}
        disabled={value === max || disabled}
        className='px-3 text-lg disabled:cursor-not-allowed disabled:text-neutral-400'
      >
        +
      </button>
    </div>
  )
}

export default CartNumberInput
